// src/utils/useGooglePay.ts
import { tracker } from 'ouisys-engine/utilities/tracker';
import { useRootContext } from '../providers/RootContext';
import searchToObject from './searchToObject';
import { useRef } from 'react';
import { processPayment } from '../services/googlePayService';

const useGooglePay = () => {
  const { setErrorMessage, config, locale, setIsLoading } = useRootContext();
  const urlParams = searchToObject();
  const { visitor } = (window as any).pac_analytics || {};
  const rockmanId = visitor?.rockmanId || '';
  const slug = `${config?.strategyConfigs?.default?.flowConfig?.slug}${urlParams['d_country'] || ''}`;
  const isProcessing = useRef(false);

  const handleError = (message: string, error: string = '') => {
    if (urlParams['debug']) {
      const fullMessage = error ? `${message}: ${error}` : message;
      alert(fullMessage);
      console.error(fullMessage);
    }
    setErrorMessage('generic-error');
  };

  const initGooglePay = async (paymentRequest: any, trigger: string) => {
    if (isProcessing.current) return;
    isProcessing.current = true;
    setIsLoading(true);

    tracker.advancedInPreFlow(trigger, { paymentRequest });

    if (urlParams['test-error']) {
      setErrorMessage('generic-error');
      return;
    }

    try {
      if (!(window as any).google?.payments?.api) {
        throw new Error('Google Pay is not available.');
      }

      const paymentsClient = new (window as any).google.payments.api.PaymentsClient({ environment: 'PRODUCTION' });
      tracker.advancedInFlow('google-pay-flow', 'payment-flow-begin', { paymentRequest });
      const paymentData = await paymentsClient.loadPaymentData(paymentRequest);

      tracker.customEvent('google-pay-flow', 'payment-flow-authorized', '');
      const result = await processPayment(paymentData, slug, locale, rockmanId);

      if (result.status === 'error') {
        tracker.customEvent('google-pay-flow', 'process-payment', 'fetch-error');
        throw new Error('Payment processing failed.');
      } else {
        tracker.customEvent('google-pay-flow', 'process-payment', 'fetch-result');
        tracker.advancedInFlow('google-pay-flow', 'payment-success');
        if (result.html) {
          document.open();
          document.write(result.html);
          document.close();
        }
      }
    } catch (error: any) {
      tracker.customEvent('google-pay-flow', 'payment-flow-catch', error?.toString());
      handleError('Error during Google Pay flow', error?.toString());
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        isProcessing.current = false;
      }, 2000);
    }
  };

  return {
    initGooglePay
  };
};

export default useGooglePay;
